import React, { useEffect, useRef, useState } from "react";
import { X, MessageSquare, MessageCircle, Mail, Copy, Check } from "lucide-react";
import { LINKS } from "../mock";

export default function ShareModal({ onClose }) {
  const url = LINKS.shareUrl;
  const message = `Check out BotsPay.me — EazyBot Trading Bots. Join a free live class: ${url}`;
  const [copied, setCopied] = useState(false);
  const urlInputRef = useRef(null);

  useEffect(() => {
    document.body.classList.add("no-scroll");
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.classList.remove("no-scroll");
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const copyLink = async () => {
    let success = false;
    try {
      await navigator.clipboard.writeText(url);
      success = true;
    } catch (error) {
      const input = urlInputRef.current;
      if (input) {
        input.focus();
        input.select();
        input.setSelectionRange(0, input.value.length);
        success = document.execCommand("copy");
      }
    }
    if (success) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    }
  };

  const tiles = [
    {
      id: "sms",
      label: "Text Message",
      icon: MessageSquare,
      color: "#0090CC",
      bg: "rgba(0,168,232,0.12)",
      href: `sms:?&body=${encodeURIComponent(message)}`,
    },
    {
      id: "whatsapp",
      label: "WhatsApp",
      icon: MessageCircle,
      color: "#1FA855",
      bg: "rgba(37,211,102,0.14)",
      href: `https://wa.me/?text=${encodeURIComponent(message)}`,
    },
    {
      id: "email",
      label: "Email",
      icon: Mail,
      color: "#9A6E1C",
      bg: "rgba(198,154,58,0.14)",
      href: `mailto:?subject=${encodeURIComponent("Join me for a free EazyBot class")}&body=${encodeURIComponent(message)}`,
    },
  ];

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="share-modal">
      <div
        className="modal-panel card-light w-full max-w-md rounded-2xl p-6 md:p-8 relative"
        onClick={(e) => e.stopPropagation()}
        style={{ marginTop: "10vh" }}
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="share-modal-close"
          className="absolute top-5 right-5 h-9 w-9 rounded-full flex items-center justify-center transition-colors"
          style={{ border: "1px solid rgba(32,24,80,0.15)", color: "#201850" }}
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-center gap-2">
          <span className="inline-block h-2 w-2 rounded-full" style={{ background: "#00A8E8" }} />
          <span className="font-mono-label text-[12px]" style={{ color: "#0090CC" }}>Share</span>
        </div>
        <h2 className="font-display mt-3" style={{ fontSize: "clamp(1.4rem, 2.6vw, 1.8rem)", color: "#201850" }}>
          Invite friends &amp; family to a class
        </h2>
        <p className="mt-1 text-sm" style={{ color: "rgba(32,24,80,0.65)" }}>
          Share BotsPay.me via your favorite app.
        </p>

        <div className="mt-6 grid grid-cols-3 gap-3">
          {tiles.map((t) => {
            const Icon = t.icon;
            return (
              <a
                key={t.id}
                href={t.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`share-${t.id}`}
                className="btn-lift flex flex-col items-center justify-center gap-2 rounded-xl py-4 px-2"
                style={{ background: "#ffffff", border: "1px solid rgba(32,24,80,0.1)" }}
              >
                <span className="h-11 w-11 rounded-full flex items-center justify-center" style={{ background: t.bg, color: t.color }}>
                  <Icon className="h-5 w-5" />
                </span>
                <span className="text-xs font-semibold" style={{ color: "#201850" }}>{t.label}</span>
              </a>
            );
          })}
        </div>

        <div className="mt-6">
          <div
            className="flex items-center gap-2 rounded-lg p-1.5 pl-3"
            style={{ background: "#ffffff", border: "1px solid rgba(32,24,80,0.12)" }}
          >
            <input ref={urlInputRef} readOnly value={url} aria-label="Share URL" className="min-w-0 flex-1 bg-transparent text-sm outline-none" style={{ color: "rgba(32,24,80,0.7)" }} data-testid="share-url-input" />
            <button
              type="button"
              onClick={copyLink}
              data-testid="share-copy"
              className="btn-lift inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md text-sm font-semibold"
              style={{ background: copied ? "#1FA855" : "#201850", color: "#ffffff" }}
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
